import React, { useState, useEffect, useMemo } from 'react';
import { useProfileImage } from '../hooks/useProfileImage';

// Generate a consistent color based on the name
const getColorFromName = (name) => {
  const colors = [
    'from-blue-500 to-blue-600',
    'from-green-500 to-green-600',
    'from-purple-500 to-purple-600',
    'from-pink-500 to-pink-600',
    'from-indigo-500 to-indigo-600',
    'from-teal-500 to-teal-600',
    'from-orange-500 to-orange-600',
    'from-red-500 to-red-600',
    'from-cyan-500 to-cyan-600',
    'from-emerald-500 to-emerald-600',
  ];

  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

/**
 * ProfileAvatar component that displays a user's profile image from S3
 * Falls back to initials in a colorful circle if no profile image is available
 */
const ProfileAvatar = ({
  userId,
  profileImage,
  firstName = '',
  lastName = '',
  size = 'md',
  className = '',
  onClick
}) => {
  const { imageUrl, loading } = useProfileImage(userId, profileImage);
  const [imgError, setImgError] = useState(false);

  // Reset error state when imageUrl changes
  useEffect(() => {
    setImgError(false);
  }, [imageUrl]);

  // Size classes for the container
  const sizeClasses = {
    xs: 'w-6 h-6',
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16',
    '2xl': 'w-20 h-20'
  };

  // Font size classes for initials
  const fontSizeClasses = {
    xs: 'text-[10px]',
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
    xl: 'text-lg',
    '2xl': 'text-xl'
  };

  const sizeClass = sizeClasses[size] || sizeClasses.md;
  const fontSizeClass = fontSizeClasses[size] || fontSizeClasses.md;

  // Generate full name and initials
  const fullName = `${firstName || ''} ${lastName || ''}`.trim() || 'User';
  const initials = fullName
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  // Get consistent color for this user
  const colorClass = useMemo(() => getColorFromName(fullName), [fullName]);

  // Show initials fallback when:
  // 1. Loading and no image yet
  // 2. No profileImage provided
  // 3. Image failed to load
  const showInitials = loading || !imageUrl || imgError;

  if (showInitials) {
    return (
      <div
        className={`${sizeClass} rounded-full flex items-center justify-center bg-gradient-to-br ${colorClass} text-white font-semibold ${className}`}
        onClick={onClick}
        style={{ cursor: onClick ? 'pointer' : 'default' }}
      >
        <span className={fontSizeClass}>{initials}</span>
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={fullName}
      className={`${sizeClass} rounded-full object-cover ${className}`}
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
      onError={() => setImgError(true)}
    />
  );
};

export default ProfileAvatar;
